import BigQueryInputField from "@/components/BigQueryInputField";
import { Input } from "@/types";
import Form, { FormField } from "@/ui-lib/components/Form";
import IconTimes from "@/ui-lib/icons/IconTimes";
import { useTheme } from "@emotion/react";
import { faInfoCircle, faWarning } from "@fortawesome/free-solid-svg-icons";
import { CustomMetricDataType } from "@ternary/api-lib/constants/enums";
import { ExportSourceConfig } from "@ternary/api-lib/core/types/GcpIntegration";
import Button from "@ternary/api-lib/ui-lib/components/Button";
import Flex from "@ternary/api-lib/ui-lib/components/Flex";
import Icon from "@ternary/api-lib/ui-lib/components/Icon";
import Text from "@ternary/api-lib/ui-lib/components/Text";
import { Tooltip } from "@ternary/api-lib/ui-lib/components/Tooltip";
import Box from "@ternary/web-ui-lib/components/Box";
import React from "react";
import Checkbox from "../../../ui-lib/components/Checkbox";
import { FileUploader } from "../../../ui-lib/components/FileUploader";
import LoadingSpinner from "../../../ui-lib/components/LoadingSpinner";
import Select from "../../../ui-lib/components/Select";
import TextInput from "../../../ui-lib/components/TextInput";
import copyText from "../copyText";

const BIG_QUERY_INPUT_TYPE = "BIG_QUERY_INPUT_TYPE";
const CSV_INPUT_TYPE = "CSV_INPUT_TYPE";

interface Props {
  bigQueryConfig: {
    bigQueryDatasetIDInput: Input<string | null>;
    bigQueryProjectIDInput: Input<string | null>;
    bigQueryTableIDInput: Input<string | null>;
  };
  bigQueryConfigError?: {
    datasetID?: boolean;
    projectID?: boolean;
    tableID?: boolean;
  };
  bigQueryDatasets?: { id: string }[];
  bigQueryTables?: { id: string }[];
  bqTableRowCountExceeded?: boolean;
  canSubmit?: boolean;
  csvInput: Input<string | null>;
  csvInputError?: string;
  externalLabelMap?: Record<string, string>;
  inputType: string;
  isLoadingBigQueryDatasets?: boolean;
  isLoadingBigQueryMetadata?: boolean;
  isLoadingBigQueryTables?: boolean;
  isNameInputValid: boolean;
  isProcessing?: boolean;
  isValidBigQueryConfig?: boolean;
  nameInput: Input<string>;
  schemaInput: Input<Record<string, string | null>>;
  selectedCustomMetric?: {
    schema: Record<string, CustomMetricDataType | null>;
  };
  selectedTypeOption?: { value: string; label: string };
  typeOptions: { value: string; label: string }[];
  onInteraction: (interaction: CustomMetricForm.Interaction) => void;
}

export function CustomMetricForm(props: Props): JSX.Element {
  const theme = useTheme();

  //
  // Computed Values
  //

  const csvSchemaOptions = [
    {
      label: copyText.customMetricFormSchemaDimensionLabel,
      value: CustomMetricDataType.DIMENSION,
    },
    {
      label: copyText.customMetricFormSchemaMeasureLabel,
      value: CustomMetricDataType.MEASURE,
    },
    {
      label: copyText.customMetricFormSchemaTimestampLabel,
      value: CustomMetricDataType.TIMESTAMP,
    },
  ];

  const headerEntries = Object.entries(props.schemaInput.value);

  return (
    <Form>
      <Flex alignItems="center">
        {/* Name Input */}
        <Box width="50%">
          <Tooltip
            content={copyText.customMetricFormInvalidNameInputTooltipLabel}
            hide={props.isNameInputValid}
            width="250px"
          >
            <FormField
              input={TextInput}
              label={
                <Flex alignItems="center">
                  <Text>{copyText.nameInputLabel}</Text>
                  <Tooltip
                    icon={faInfoCircle}
                    content={copyText.customMetricFormNameInputTooltipLabel}
                    placement="auto"
                  />
                </Flex>
              }
              value={props.nameInput.value}
              variant={props.isNameInputValid ? undefined : "danger"}
              onChange={(event) =>
                props.onInteraction({
                  type: CustomMetricForm.INTERACTION_UPDATE_NAME,
                  value: event.target.value,
                })
              }
            />
          </Tooltip>
        </Box>
        <Box paddingLeft={theme.space_md} width="50%">
          <FormField label={copyText.customMetricFormTypeInputLabel}>
            <Select
              options={props.typeOptions}
              value={props.selectedTypeOption}
              onChange={(option) => {
                props.onInteraction({
                  type: CustomMetricForm.INTERACTION_UPDATE_FORM_TYPE,
                  value: option?.value ?? "",
                });
              }}
            />
          </FormField>
        </Box>
      </Flex>

      {/* BigQuery Input*/}
      {props.inputType === BIG_QUERY_INPUT_TYPE && (
        <Box marginBottom={theme.space_sm}>
          <BigQueryInputField
            bigQueryDatasets={props.bigQueryDatasets}
            bigQueryTables={props.bigQueryTables}
            error={props.bigQueryConfigError}
            value={{
              datasetID:
                props.bigQueryConfig.bigQueryDatasetIDInput.value ?? null,
              projectID:
                props.bigQueryConfig.bigQueryProjectIDInput.value ?? null,
              tableID: props.bigQueryConfig.bigQueryTableIDInput.value ?? null,
            }}
            isLoadingDatasets={props.isLoadingBigQueryDatasets}
            isLoadingTables={props.isLoadingBigQueryTables}
            onChange={(fieldName, value) => {
              props.onInteraction({
                type: CustomMetricForm.INTERACTION_UPDATE_BIGQUERY_CONFIG,
                fieldName,
                value,
              });
            }}
          />
          <Flex alignItems="center">
            <Button
              disabled={!props.isValidBigQueryConfig}
              marginRight={theme.space_sm}
              secondary
              size="small"
              width={100}
              type="button"
              onClick={() =>
                props.onInteraction({
                  type: CustomMetricForm.INTERACTION_VALIDATE_BUTTON_CLICKED_UPDATE,
                  bigQueryConfig: {
                    datasetID:
                      props.bigQueryConfig.bigQueryDatasetIDInput.value ?? "",
                    projectID:
                      props.bigQueryConfig.bigQueryProjectIDInput.value ?? "",
                    tableID:
                      props.bigQueryConfig.bigQueryTableIDInput.value ?? "",
                  },
                })
              }
            >
              {props.isLoadingBigQueryMetadata ? (
                <LoadingSpinner />
              ) : (
                copyText.customMetricFormValidateLabel
              )}
            </Button>
            {props.bqTableRowCountExceeded ? (
              <Tooltip
                content={
                  copyText.customMetricFormBigQueryTableLimitExceededMessage
                }
                width="250px"
              >
                <Icon
                  color={theme.tag_button_background_color_danger_hover}
                  icon={faWarning}
                />
              </Tooltip>
            ) : null}
          </Flex>
        </Box>
      )}

      {/* CSV Input */}
      {props.inputType === CSV_INPUT_TYPE && (
        <Flex alignItems="center">
          <FormField
            label={
              <Flex alignItems="center">
                <Text>{copyText.customMetricFormCsvUploadLabel}</Text>
                <Tooltip
                  icon={faInfoCircle}
                  content={copyText.customMetricFormCsvDocumentationLabel}
                  placement="auto"
                />
              </Flex>
            }
          >
            <Box minWidth={350}>
              <FileUploader
                acceptedFileTypes={["CSV"]}
                errorCaption={props.csvInputError}
                onChange={(file) =>
                  props.onInteraction({
                    type: CustomMetricForm.INTERACTION_FILE_UPLOAD,
                    file,
                  })
                }
              />
            </Box>
          </FormField>
        </Flex>
      )}

      {/* Schema Input */}

      {Object.keys(props.schemaInput.value).length > 0 && (
        <FormField label={copyText.customMetricFormDataTypeInputLabel} required>
          <Box marginTop={theme.space_sm}>
            {headerEntries.map(([headerKey, headerValue]) => {
              const isValidDimension =
                headerValue === CustomMetricDataType.DIMENSION
                  ? !Object.keys(props.externalLabelMap ?? {}).some(
                      (label) => label === headerKey
                    ) ||
                    (props.selectedCustomMetric !== undefined &&
                      Object.entries(props.selectedCustomMetric.schema).some(
                        ([key, value]) =>
                          headerKey === key &&
                          value === CustomMetricDataType.DIMENSION
                      ))
                  : true;

              return (
                <Flex
                  key={`${headerKey}-${headerValue}`}
                  justifyContent="space-between"
                  marginBottom={theme.space_md}
                >
                  <Checkbox
                    checked={props.schemaInput.value[headerKey] !== null}
                    onChange={(event) =>
                      props.onInteraction({
                        type: CustomMetricForm.INTERACTION_TOGGLE_SCHEMA_CHECKBOX,
                        checked: event.target.checked,
                        headerKey,
                      })
                    }
                  />
                  <Box paddingLeft={theme.space_md} width="50%">
                    <Tooltip
                      content={
                        copyText.customMetricFormInvalidDimensionInputTooltipLabel
                      }
                      hide={isValidDimension}
                      width="200px"
                    >
                      <TextInput
                        disabled
                        value={headerKey}
                        variant={isValidDimension ? undefined : "danger"}
                      />
                    </Tooltip>
                  </Box>

                  <Flex
                    alignItems="center"
                    paddingLeft={theme.space_md}
                    width="50%"
                  >
                    <Select
                      disabled={props.schemaInput.value[headerKey] === null}
                      options={csvSchemaOptions.filter(
                        (option) =>
                          !Object.values(props.schemaInput.value).some(
                            (value) =>
                              value !== CustomMetricDataType.DIMENSION &&
                              option.value === value
                          )
                      )}
                      placeholder={
                        copyText.customMetricFormSchemaSelectPlaceholder
                      }
                      value={csvSchemaOptions.find(
                        (option) => option.value === headerValue
                      )}
                      onChange={(option) =>
                        option &&
                        props.onInteraction({
                          type: CustomMetricForm.INTERACTION_UPDATE_SCHEMA,
                          header: headerKey,
                          value: option.value,
                        })
                      }
                    />
                    <Button
                      disabled={
                        props.schemaInput.value[headerKey] === undefined
                      }
                      iconStart={<IconTimes />}
                      size="tiny"
                      type="button"
                      onClick={() =>
                        props.onInteraction({
                          type: CustomMetricForm.INTERACTION_UPDATE_SCHEMA,
                          header: headerKey,
                          value: "",
                        })
                      }
                    />
                  </Flex>
                </Flex>
              );
            })}
          </Box>
        </FormField>
      )}

      <Flex justifyContent="flex-end" marginTop={theme.space_sm}>
        <Button
          secondary
          type="reset"
          width={100}
          onClick={() =>
            props.onInteraction({
              type: CustomMetricForm.INTERACTION_CANCEL_BUTTON_CLICKED,
            })
          }
        >
          {copyText.cancelButtonLabel}
        </Button>
        <Button
          disabled={
            !props.canSubmit ||
            props.isProcessing ||
            props.bqTableRowCountExceeded
          }
          marginLeft={theme.space_sm}
          primary
          width={100}
          type="button"
          onClick={() =>
            props.onInteraction({
              type: CustomMetricForm.INTERACTION_SUBMIT_BUTTON_CLICKED,
            })
          }
        >
          {props.isProcessing ? <LoadingSpinner /> : copyText.submitButtonLabel}
        </Button>
      </Flex>
    </Form>
  );
}

CustomMetricForm.INTERACTION_CANCEL_BUTTON_CLICKED =
  "CreateCustomMetricForm.INTERACTION_CANCEL_BUTTON_CLICKED" as const;

CustomMetricForm.INTERACTION_SUBMIT_BUTTON_CLICKED =
  "CreateCustomMetricForm.INTERACTION_SUBMIT_BUTTON_CLICKED" as const;

CustomMetricForm.INTERACTION_SUBMIT_BUTTON_CLICKED_CREATE =
  "CreateCustomMetricForm.INTERACTION_SUBMIT_BUTTON_CLICKED_CREATE" as const;

CustomMetricForm.INTERACTION_SUBMIT_BUTTON_CLICKED_UPDATE =
  "CreateCustomMetricForm.INTERACTION_SUBMIT_BUTTON_CLICKED_UPDATE" as const;

CustomMetricForm.INTERACTION_VALIDATE_BUTTON_CLICKED_UPDATE =
  "CreateCustomMetricForm.INTERACTION_VALIDATE_BUTTON_CLICKED_UPDATE" as const;

CustomMetricForm.INTERACTION_UPDATE_BIGQUERY_CONFIG =
  "CreateCustomMetricForm.INTERACTION_UPDATE_BIGQUERY_CONFIG" as const;

CustomMetricForm.INTERACTION_UPDATE_NAME =
  "CreateCustomMetricForm.INTERACTION_UPDATE_NAME" as const;

CustomMetricForm.INTERACTION_UPDATE_FORM_TYPE =
  "CreateCustomMetricForm.INTERACTION_UPDATE_FORM_TYPE" as const;

CustomMetricForm.INTERACTION_FILE_UPLOAD =
  "CreateCustomMetricForm.INTERACTION_FILE_UPLOAD" as const;

CustomMetricForm.INTERACTION_UPDATE_SCHEMA =
  "CreateCustomMetricForm.INTERACTION_UPDATE_SCHEMA" as const;

CustomMetricForm.INTERACTION_TOGGLE_SCHEMA_CHECKBOX =
  "CreateCustomMetricForm.INTERACTION_TOGGLE_SCHEMA_CHECKBOX" as const;

type InteractionCancelButtonClicked = {
  type: typeof CustomMetricForm.INTERACTION_CANCEL_BUTTON_CLICKED;
};

type InteractionSubmitButtonClicked = {
  type: typeof CustomMetricForm.INTERACTION_SUBMIT_BUTTON_CLICKED;
};

type InteractionSubmitButtonClickedCreate = {
  type: typeof CustomMetricForm.INTERACTION_SUBMIT_BUTTON_CLICKED_CREATE;
  bigQueryConfig: ExportSourceConfig | null;
  csv: string | null;
  name: string;
  schema: Record<string, CustomMetricDataType | null>;
};

type InteractionSubmitButtonClickedUpdate = {
  type: typeof CustomMetricForm.INTERACTION_SUBMIT_BUTTON_CLICKED_UPDATE;
  csv?: string | null;
  name?: string;
  schema?: Record<string, CustomMetricDataType | null>;
};

type InteractionValidateButtonClicked = {
  type: typeof CustomMetricForm.INTERACTION_VALIDATE_BUTTON_CLICKED_UPDATE;
  bigQueryConfig: ExportSourceConfig;
};

type InteractionUpdateBigQueryConfig = {
  type: typeof CustomMetricForm.INTERACTION_UPDATE_BIGQUERY_CONFIG;
  fieldName: string;
  value: string | null;
};

type InteractionUpdateName = {
  type: typeof CustomMetricForm.INTERACTION_UPDATE_NAME;
  value: string;
};

type InteractionFileUpload = {
  type: typeof CustomMetricForm.INTERACTION_FILE_UPLOAD;
  file: File | null;
};

type InteractionUpdateFormType = {
  type: typeof CustomMetricForm.INTERACTION_UPDATE_FORM_TYPE;
  value: string;
};

type InteractionUpdateSchema = {
  type: typeof CustomMetricForm.INTERACTION_UPDATE_SCHEMA;
  header: string;
  value: string;
};

type InteractionToggleSchemaCheckbox = {
  type: typeof CustomMetricForm.INTERACTION_TOGGLE_SCHEMA_CHECKBOX;
  checked: boolean;
  headerKey: string;
};

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace CustomMetricForm {
  export type Interaction =
    | InteractionCancelButtonClicked
    | InteractionSubmitButtonClicked
    | InteractionSubmitButtonClickedCreate
    | InteractionSubmitButtonClickedUpdate
    | InteractionValidateButtonClicked
    | InteractionUpdateBigQueryConfig
    | InteractionUpdateFormType
    | InteractionUpdateName
    | InteractionFileUpload
    | InteractionUpdateSchema
    | InteractionToggleSchemaCheckbox;
}

export default CustomMetricForm;
